import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import isEqual from 'lodash/isEqual'

import apiClient from '@/services/axios'
import debounce from 'lodash.debounce'

import general from './general'
import content from './content'
import filter from './filter'
import settings from './settings'
import { CUSTOM_PAGE_SETTINGS_FIELD, FILTER_OPTIONS } from '@/constants/custom-pages'

import {
  formColorsToDTO,
  formWidgetToDTO,
  formChampToDTO,
  formEventToDTO,
} from '@/services/utils/custom-pages'

Vue.use(Vuex)

const getDefaultState = () => ({
  fetchedData: null,
  fetchedDataLoading: false,
  isPageFilled: false,
  isActive: false,
  isPublished: false,
  isPublishable: false,
  sports: [],
  sportsSearchCache: {},
  showAllCategoriesTitle: false,
})

export default {
  modules: {
    general,
    content,
    filter,
    settings,
  },
  state: getDefaultState(),
  mutations: {
    SET_FETCHED_DATA(state, payload) {
      state.fetchedData = payload
    },
    SET_FETCHED_DATA_LOADING(state, isLoading) {
      state.fetchedDataLoading = isLoading
    },
    SET_IS_PAGE_FILLED(state, flag) {
      state.isPageFilled = flag
    },
    SET_SPORTS(state, sports) {
      state.sports = sports
    },
    SET_SPORTS_CACHE(state, { queryKey, data }) {
      state.sportsSearchCache[queryKey] = data
    },
    SET_IS_ACTIVE(state, flag) {
      state.isActive = flag
    },
    SET_IS_PUBLISHED(state, flag) {
      state.isPublished = flag
    },
    SET_IS_PUBLISHABLE(state, flag) {
      state.isPublishable = flag
    },
    SET_SHOW_ALL_CATEGORIES_TITLE(state, flag) {
      state.showAllCategoriesTitle = flag
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    resetState({ commit }) {
      commit('RESET_STATE')
      commit('general/RESET_STATE')
      commit('content/RESET_STATE')
      commit('filter/RESET_STATE')
      commit('settings/RESET_STATE')
    },
    async getLangs({ dispatch, rootGetters }) {
      if (!rootGetters['langs']?.langData?.length) {
        await dispatch('GET_LANGS', null, { root: true })
      }
    },
    async fetchData({ commit }, uuid) {
      commit('SET_FETCHED_DATA_LOADING', true)
      try {
        const response = await apiClient.get(`/admin/custom-page/${uuid}`)
        commit('SET_FETCHED_DATA', response?.data?.data || null)
      } catch (error) {
        console.error('Failed to fetch data:', error)
        commit('SET_FETCHED_DATA', null)
      } finally {
        commit('SET_FETCHED_DATA_LOADING', false)
      }
    },
    async fetchSports({ commit, state }, params = {}) {
      const queryKey = params.searchValue || ''
      const url = '/admin/sports'

      if (state.sportsSearchCache[queryKey]) {
        commit('SET_SPORTS', state.sportsSearchCache[queryKey])
        return
      }

      try {
        const resp = await apiClient.get(url, {
          params: {
            ...params,
            results: 200,
          },
        })
        commit('SET_SPORTS', resp.data.data)
        commit('SET_SPORTS_CACHE', { queryKey, data: resp.data.data })
      } catch (e) {
        console.error('Error fetching icons:', e)
      }
    },
    debouncedFetchSports: debounce(({ dispatch }, query) => {
      const queryKey = query || ''
      dispatch('fetchSports', {
        searchField: 'name',
        searchValue: queryKey,
      })
    }, 700),
    setIsActive({ commit }, flag) {
      commit('SET_IS_ACTIVE', flag)
    },
    setIsPublished({ commit }, flag) {
      commit('SET_IS_PUBLISHED', flag)
    },
    setIsPublishable({ commit }, flag) {
      commit('SET_IS_PUBLISHABLE', flag)
    },
    fillGlobal({ commit }, data) {
      commit('SET_IS_ACTIVE', data.is_active)
      commit('SET_IS_PUBLISHED', data.is_publish)
      commit('SET_IS_PUBLISHABLE', data.is_publishable)
      commit('SET_SHOW_ALL_CATEGORIES_TITLE', data.show_category_title ?? false)
    },
    async fillCustomPage({ dispatch, commit }, data) {
      commit('SET_IS_PAGE_FILLED', false)
      await Promise.all([
        dispatch('fillGlobal', data),
        dispatch('general/fillForm', data),
        dispatch('content/fillHeader', data),
        dispatch('content/fillBlocks', data),
        dispatch('filter/fillFilters', data),
        dispatch('settings/fillSettings', data),
      ])
      commit('SET_IS_PAGE_FILLED', true)
    },
    async createPayload({ state, rootGetters }) {
      const uuid = state.fetchedData.uuid
      if (!uuid) return

      const langs = rootGetters['langs']?.langData
      const langMap = Object.fromEntries(langs?.map((l) => [l.name, l.id]))

      const mapLangId = (code) => langMap[code]

      const getBlocks = () => {
        const normalized = []
        const origBlocks = state.content.content.blocks

        for (const [langCode, blocks] of Object.entries(origBlocks)) {
          const lid = mapLangId(langCode)
          if (lid == null) continue

          for (const block of blocks) {
            let target = normalized.find((b) => b.id === block.id)

            if (!target) {
              target = {
                id: block.id,
                type: block.type === 'widget' ? 'banner' : block.type,
                langs: [],
              }

              if (target.type === 'category') target.items = []

              normalized.push(target)
            }

            if (block.type === 'category') {
              target.langs.push({
                lid,
                title: block.title,
                icon_id: block.selectedIcon?.id ?? null,
                icon_color: block.iconColor,
                show_championship: block.showTournaments,
              })

              for (const child of block.children ?? []) {
                if (child.type === 'tournament') {
                  const champBlock = formChampToDTO(child, lid)

                  champBlock.items = []

                  target.items.push(champBlock)

                  for (const match of child.children ?? []) {
                    if (match.type === 'match') {
                      champBlock.items.push(formEventToDTO(match, lid))
                    }
                    if (match.type === 'widget') {
                      champBlock.items.push(formWidgetToDTO(match, lid))
                    }
                  }
                }

                if (child.type === 'match') {
                  target.items.push(formEventToDTO(child, lid))
                }
                if (child.type === 'widget') {
                  target.items.push(formWidgetToDTO(child, lid))
                }
              }
            }

            if (block.type === 'widget') {
              target.langs.push(formWidgetToDTO(block, lid).langs[0])
            }
          }
        }

        return normalized
      }

      const getFilters = () => {
        const normalized = []
        const origFilters = state.filter.form ?? {}

        for (const [langCode, f] of Object.entries(origFilters)) {
          const lid = mapLangId(langCode)
          if (!f?.showFilter) continue

          for (const block of f.filterBlocks) {
            let filter = normalized.find(
              (fl) => fl.type === block.id || fl.id === block.id,
            )

            if (!filter) {
              filter = { langs: [] }
              normalized.push(filter)
            }

            filter.type = FILTER_OPTIONS.map(o => o.value).includes(
              block.id,
            )
              ? block.id
              : 'custom'

            const langData = {
              lid,
              title: block.title,
              ...(filter.type === 'custom' && { id: block.id }),
            }

            if (block.custom && block.children) {
              langData.matches = block.children.map((ch) => ch.id ?? ch)
            }

            filter.langs.push(langData)
          }
        }

        return { filters: normalized, show_filters: !!normalized.length }
      }

      const getHeader = () => {
        const origHeader = state.content.header ?? {}
        const image = { langs: [] }
        const buttonMap = {}

        for (const [langCode, h] of Object.entries(origHeader)) {
          const lid = mapLangId(langCode)

          if (typeof h?.image?.id === 'number') {
            image.langs.push({
              lid,
              header_banner_background_id: origHeader[langCode].image?.id,
            })
          }

          for (const btn of h.buttons ?? []) {
            if (!buttonMap[btn.id]) {
              buttonMap[btn.id] = { langs: [] }
            }
            buttonMap[btn.id].langs.push({
              lid,
              title: btn.title,
              link: btn.deeplink,
            })
          }
        }

        const buttons = Object.values(buttonMap)

        return {
          ...image,
          ...(buttons?.length && { header: { buttons } }),
        }
      }

      const getSettings = () => {
        const resolveThemeSettings = (s, customThemes) => {
          const themeObj = customThemes.find((t) => t.id === s.theme)

          // system theme
          if (themeObj.is_default) {
            return { theme_id: s.theme }
          }

          // matchTheme is system
          if (s.matchTheme === 'system') {
            return { theme_id: s.theme, match_theme: s.matchTheme }
          }

          // normalize colors
          const settingsColors = formColorsToDTO(s)

          // if custom theme colors === settings colors => pass theme id
          const {
            id,
            is_default,
            lid,
            match_theme,
            name,
            ...customThemeColors
          } = themeObj

          if (isEqual(customThemeColors, settingsColors)) {
            return { theme_id: s.theme, match_theme: s.matchTheme }
          }

          return settingsColors
        }

        const origSettings = state.settings.form ?? {}
        const customThemes = state.settings.customThemes
        const settings = []

        for (const [langCode, s] of Object.entries(origSettings)) {
          if (
            !s ||
            JSON.stringify(s) === JSON.stringify(CUSTOM_PAGE_SETTINGS_FIELD)
          ) {
            continue
          }

          const lid = mapLangId(langCode)
          const themeSettings = resolveThemeSettings(s, customThemes)

          settings.push({
            lid,
            ...themeSettings,
          })
        }

        return settings
      }

      const getGeneral = () => {
        const generalForm = state.general.form

        const name = generalForm.name || state.fetchedData.name

        const applications = generalForm.apps.length
          ? generalForm.apps
          : state.fetchedData.applications

        const segments = generalForm.segments.length
          ? generalForm.segments
          : state.fetchedData.segments.map((s) => s.id)

        const period =
          generalForm?.period?.start && generalForm?.period?.end
            ? {
              starts_at: moment(generalForm.period.start)
                .tz('Etc/UTC')
                .format('YYYY-MM-DD'),
              ends_at: moment(generalForm.period.end)
                .tz('Etc/UTC')
                .format('YYYY-MM-DD'),
            }
            : state.fetchedData.period

        return {
          name,
          is_active: state.isActive ?? state.fetchedData.is_active,
          is_published: state.isPublished ?? state.fetchedData.is_active,
          show_category_title: state.showAllCategoriesTitle,
          applications,
          segments,
          period,
        }
      }

      const content = {
        ...getGeneral(),
        ...getHeader(),
        ...getFilters(),
        blocks: getBlocks(),
        settings: getSettings(),
      }

      return content
    },
    async initPage({ dispatch, state }, uuid) {
      await dispatch('getLangs')

      if (uuid) {
        dispatch('fetchData', uuid)
        await dispatch('fetchSports')
      }

      dispatch('content/initForm') // header
      dispatch('filter/initForm')
      dispatch('settings/initForm')

      await dispatch('general/fetchSegments')
      await dispatch('settings/fetchThemes')
      await dispatch('fillCustomPage', state.fetchedData)
    },
  },
  getters: {
    fetchedData: (state) => state.fetchedData,
    fetchedDataLoading: (state) => state.fetchedDataLoading,
    sportsSearchCache: (state) => state.sportsSearchCache,
    isActive: (state) => state.isActive,
    isPublished: (state) => state.isPublished,
    isPublishable: (state) => state.isPublishable,
    isPageFilled: (state) => state.isPageFilled,
    showAllCategoriesTitle: (state) => state.showAllCategoriesTitle,
  },
}
